import * as React from 'react';
import * as clone from 'clone';
import { Button, Drawer, Well, FileUploader, executeCleanDrawerRender } from '@pushly/aqe/lib/components';
import {
    DeleteFilled,
    EditOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    SettingOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, InputNumber, Skeleton, Button as AntButton } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { Container } from 'typescript-ioc/es5';
import * as deepEqual from 'react-fast-compare';
import * as randomstring from 'randomstring';
import 'jsoneditor-react/es/editor.min.css';
import { isDef } from '@pushly/aqe/lib/utils/is-def';
import { DomainDto } from '../../dtos/domain';
import { AppState } from '../../stores/app';
import { AppService, DomainService } from '../../services';
import { getClassNames } from '../../_utils/classnames';
import { noop, simpleFormErrorNotification, simpleNotification } from '../../_utils/utils';
import {
    calculateRawTtlSecondsFromValue,
    TtlDisplayElement,
    TtlEditorElement
} from '../data-form-elements';
import { validateFields } from '../../_utils/antd';
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants';
import {
    getCustomMacroFields,
    getDomainDefaultActionLabels,
    parseDefaultNotificationAction,
    prepareWhitelistDomains
} from '../../_utils/domain';
import { INotificationAction } from '../../interfaces/notification-action';
import { NotificationActionBuilder } from '../notification-action-builder/notification-action-builder';
import { waitForDefined } from '../../_utils/wait-for-defined';
import TemplatizedTextField from '../templatized-text-field/templatized-text-field';
import { DomainDeliveryDisplay, DomainDeliveryEditor } from '../domain-data-form/domain-delivery-element';
import StackedTagSelect from '../stacked-tag-select/stacked-tag-select';
import { IAddedNotificationQueryParam } from "../../interfaces/added-notification-query-param";
import { NoTranslate } from '../no-translate/no-translate';

interface IDomainNotificationEditor extends FormComponentProps {
    domain: DomainDto,
    visible: boolean,
    onClose: (e: any) => void,
    onDomainUpdated: (domain: Partial<DomainDto>) => void,
}

interface IDNEState {
    defaultActions?: INotificationAction[],
    customMacroOptions?: any[],
    customUtmParams: [string, IAddedNotificationQueryParam][],
}

/**
 * @Property default_open_url_label
 * Hidding in public UI. Will re-enable if requests come in.
 *
 * The Open URL Label is the default label for ALL Open URL buttons.
 * In comparison - default button settings apply only to the specific
 * ordinal button: Button 1 or Button 2.
 * These default buttons can be removed and replaced - if a new Open URL
 * button is added or replaces a default button the default_open_url_label
 * is used in that scenario.
 *
 * TODO: if the label option is later removed please remove this comment/constant as well.
 */
const DEFAULT_OPEN_URL_LABEL_ENABLED = false;

export const DomainNotificationEditor = Form.create<IDomainNotificationEditor>()(
    class extends React.Component<IDomainNotificationEditor, IDNEState> {
        public state: IDNEState = {
            customUtmParams: [],
        };

        protected appState: AppState;
        protected appService: AppService;
        protected domainService: DomainService;

        public constructor(props: IDomainNotificationEditor) {
            super(props);

            this.appState = Container.get(AppState);
            this.appService = Container.get(AppService);
            this.domainService = Container.get(DomainService);

            executeCleanDrawerRender(this);
        }

        public async componentDidMount() {
            const customMacroOptions = await getCustomMacroFields(this.props.domain.id);
            const customUtmParams = Object.entries(clone(this.props.domain.addedNotificationQueryParams ?? {}));

            this.setState({
                customMacroOptions,
                customUtmParams,
            });
        }

        public render() {
            const currentValues = this.getCurrentValues();
            const domainDefaultLabels = getDomainDefaultActionLabels(this.props.domain);
            const defaultActions = this.state.defaultActions ?? this.props.domain.defaultNotificationActions ?? [];
            const primaryAction: any = parseDefaultNotificationAction(this.props.domain, 0, defaultActions);
            const secondaryAction: any = parseDefaultNotificationAction(this.props.domain, 1, defaultActions);

            const customPrimaryDefined = defaultActions.find(a => a.ordinal === 0);
            if (!customPrimaryDefined) {
                defaultActions.push(primaryAction);
            }

            const utmMacroTypes = ['domain', 'device', 'location', 'notification', 'custom'];
            const isChildDomain = this.props.domain.prodDomainId !== null;

            return (
                <Drawer
                    getContainer={this.appService.getAppContainer}
                    className={getClassNames('ddf-notif-settings-editor')}
                    title="Update General Settings"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.handleDrawerClose}
                    visible={this.props.visible}
                >
                    <Form { ...DEFAULT_DRAWER_FORM_ITEM_LAYOUT } className="hide-inline-errors">
                        <Well
                            title="Landing URLs"
                            mode="ghost"
                            hideFooter={true}
                        >
                            <Form.Item label="Domain Whitelist" className={getClassNames('ddf-notif-editor-whitelist')}>
                                {this.props.form.getFieldDecorator('landingUrlDomainWhitelist', {
                                    initialValue: this.domain.landingUrlDomainWhitelist ?? [],
                                })(
                                    <StackedTagSelect<string[]>
                                        size="small"
                                        mode="tags"
                                        className="domains-allowed"
                                        placeholder="* (Any domain)"
                                        getPopupContainer={this.appService.getAppContainer}
                                        tokenSeparators={[',', ';', ' ', '\n']}
                                        showOptions={false}
                                        notFoundContent=""
                                    />
                                )}
                                <div className={getClassNames('form-layout-row-sub')}>
                                    <InfoCircleOutlined />
                                    <span>
                                        Enter each domain on a separate line that should be
                                        allowed in notification landing URLs. Leave blank to
                                        allow all domains.
                                    </span>
                                </div>
                            </Form.Item>
                        </Well>

                        <Well
                            title="Button Defaults"
                            mode="ghost"
                            hideFooter={true}
                        >
                            {DEFAULT_OPEN_URL_LABEL_ENABLED && (
                                <Form.Item label="Open URL Label" className={getClassNames('ddf-notif-editor-action-label')}>
                                    {this.props.form.getFieldDecorator('defaultOpenUrlLabel', {
                                        initialValue: domainDefaultLabels.openUrl,
                                        rules: [
                                            {
                                                required: true,
                                                message: 'The default Open URL label cannot be empty.',
                                            },
                                        ],
                                    })(
                                        <Input
                                            size="small"
                                            autoComplete="off"
                                        />
                                    )}
                                </Form.Item>
                            )}

                            <Form.Item label="Buttons">
                                <div className={getClassNames('notif-action-builder-collection')}>
                                    <NotificationActionBuilder
                                        enabled={true}
                                        removable={true}
                                        removeLabel="reset"
                                        title="Button 1"
                                        addActionIcon={<SettingOutlined />}
                                        addActionText="Set Button 1"
                                        config={primaryAction}
                                        onChange={this.handleActionUpdate}
                                        onRemove={this.handleActionRemove}
                                        onAdd={this.handlePrimaryActionAdd}
                                        rootActions={defaultActions}
                                        customMacroOptions={waitForDefined(() => this.state.customMacroOptions!)}
                                    />

                                    <NotificationActionBuilder
                                        enabled={!!secondaryAction}
                                        removable={true}
                                        title="Button 2"
                                        addActionIcon={<SettingOutlined />}
                                        addActionText="Set Button 2"
                                        config={secondaryAction}
                                        onChange={this.handleActionUpdate}
                                        onRemove={this.handleActionRemove}
                                        onAdd={this.handleSecondaryActionAdd}
                                        rootActions={defaultActions}
                                        customMacroOptions={waitForDefined(() => this.state.customMacroOptions!)}
                                    />
                                </div>
                            </Form.Item>
                        </Well>

                        {this.appUserIsInternalDomainEditor && (
                            <Well
                                title="Delivery Defaults"
                                mode="ghost"
                                hideFooter={true}
                            >
                                <TtlEditorElement form={this.props.form} entity={this.domain} />
                                <DomainDeliveryEditor form={this.props.form} entity={this.domain} />
                            </Well>
                        )}

                        <Well
                            title="Media Defaults"
                            mode="ghost"
                            hideFooter={true}
                        >
                            {this.appUserIsInternalDomainEditor && (
                                <Form.Item label="Upload Quality" className={getClassNames('ddf-notif-editor-media-quality')}>
                                    {this.props.form.getFieldDecorator('mediaQuality', {
                                        initialValue: this.domain.mediaQuality || 40,
                                    })(
                                        <InputNumber
                                            size="small"
                                            min={30}
                                            max={100}
                                        />
                                    )}
                                </Form.Item>
                            )}

                            <div className="media-row">
                                <Form.Item label="Notification Icon" className={getClassNames('ddf-notif-editor-media-icon')}>
                                    <div className={getClassNames('form-layout-row')}>
                                        {this.props.form.getFieldDecorator('defaultIcon', {
                                            initialValue: !this.domain.defaultIconUrl ? undefined : {
                                                url: this.domain.defaultIconUrl,
                                            },
                                        })(
                                            <FileUploader
                                                className="icon-upload"
                                                type="image"
                                                disabled={isChildDomain}
                                                ownerType="domain"
                                                ownerId={this.domain.id}
                                                imageConfig={{
                                                    cropMax: FileUploader.defaultDimensions.notificationIcon,
                                                    cropRatio: 1,
                                                    quality: this.domain.mediaQuality,
                                                }}
                                                onSuccess={noop}
                                                onRemove={noop}
                                                onError={this.handleFileUploadError}
                                            />
                                        )}
                                        {isChildDomain ? (
                                            <div className={getClassNames('form-layout-row-sub')}>
                                                <InfoCircleOutlined />
                                                <span>
                                                    Icon cannot be updated on a test domain - Overlay
                                                    Icon can be updated in the Details section.
                                                </span>
                                            </div>
                                        ) : (
                                            <div className={getClassNames('form-layout-row-sub')}>
                                                <InfoCircleOutlined />
                                                <span>
                                                    This icon will be shown for each push
                                                    notification sent to a subscriber. Recommended
                                                    size is 256x256 with an aspect ratio of 1:1.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </Form.Item>

                                <Form.Item label="Notification Badge" className={getClassNames('ddf-notif-editor-media-badge')}>
                                    <div className={getClassNames('form-layout-hrow')}>
                                        {this.props.form.getFieldDecorator('defaultBadge', {
                                            initialValue: !this.domain.defaultBadgeUrl ? undefined : {
                                                url: this.domain.defaultBadgeUrl,
                                            },
                                        })(
                                            <FileUploader
                                                className="badge-upload"
                                                type="image"
                                                ownerType="domain"
                                                ownerId={this.domain.id}
                                                imageConfig={{
                                                    cropMax: FileUploader.defaultDimensions.notificationBadge,
                                                    cropRatio: 1,
                                                    quality: this.domain.mediaQuality,
                                                }}
                                                onSuccess={noop}
                                                onRemove={noop}
                                                onError={this.handleFileUploadError}
                                            />
                                        )}
                                        <div className={getClassNames('form-layout-row-sub')}>
                                            <InfoCircleOutlined />
                                            <span>
                                                Badges are only applicable to notifications
                                                delivered on Android. Images should use alpha
                                                transparency and all colors will be converted to
                                                black and white. Recommended size is 128x128 with
                                                and aspect ratio of 1:1.
                                            </span>
                                        </div>
                                    </div>
                                </Form.Item>
                            </div>
                        </Well>

                        <Well
                            title="UTM Parameters"
                            mode="ghost"
                            hideFooter={true}
                        >
                            <div className={getClassNames('ddf-notif-editor-utm-source')} >
                                {this.props.form.getFieldDecorator('utmSource', {
                                    initialValue: !this.domain.utmParams?.utm_source ? undefined :
                                        this.domain.utmParams.utm_source,
                                })(
                                    <TemplatizedTextField
                                        label="utm_source"
                                        placeholder="utm source"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        customMacroOptions={this.state.customMacroOptions}
                                        size="small"
                                    />
                                )}
                            </div>
                            <div className={getClassNames('ddf-notif-editor-utm-medium')}>
                                {this.props.form.getFieldDecorator('utmMedium', {
                                    initialValue: !this.domain.utmParams?.utm_medium ? undefined :
                                        this.domain.utmParams.utm_medium,
                                })(
                                    <TemplatizedTextField
                                        label="utm_medium"
                                        placeholder="utm medium"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        customMacroOptions={this.state.customMacroOptions}
                                        size="small"
                                    />
                                )}
                            </div>
                            <div className={getClassNames('ddf-notif-editor-utm-campaign')}>
                                {this.props.form.getFieldDecorator('utmCampaign', {
                                    initialValue: !this.domain.utmParams?.utm_campaign ? undefined :
                                        this.domain.utmParams.utm_campaign,
                                })(
                                    <TemplatizedTextField
                                        label="utm_campaign"
                                        placeholder="utm campaign"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        customMacroOptions={this.state.customMacroOptions}
                                        size="small"
                                    />
                                )}
                            </div>
                            <div className={getClassNames('ddf-notif-editor-utm-term')}>
                                {this.props.form.getFieldDecorator('utmTerm', {
                                    initialValue: !this.domain.utmParams?.utm_term ? undefined :
                                        this.domain.utmParams.utm_term,
                                })(
                                    <TemplatizedTextField
                                        label="utm_term"
                                        placeholder="utm term"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        customMacroOptions={this.state.customMacroOptions}
                                        size="small"
                                    />
                                )}
                            </div>
                            <div className={getClassNames('ddf-notif-editor-utm-content')}>
                                {this.props.form.getFieldDecorator('utmContent', {
                                    initialValue: !this.domain.utmParams?.utm_content ? undefined :
                                        this.domain.utmParams.utm_content,
                                })(
                                    <TemplatizedTextField
                                        label="utm_content"
                                        placeholder="utm content"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        customMacroOptions={this.state.customMacroOptions}
                                        size="small"
                                    />
                                )}
                            </div>
                        </Well>

                        <Well
                            key={this.state.customUtmParams.length}
                            className="custom-utm-parameter-well"
                            title="UTM Custom Parameters"
                            mode="ghost"
                            actions={(
                                <AntButton
                                    className="custom-utm-add-btn"
                                    size="small"
                                    onClick={this.handleCustomUtmAdd}
                                    disabled={Object.keys(this.state.customUtmParams).length >= 5}
                                >
                                    <PlusOutlined />
                                    <span>Add</span>
                                </AntButton>
                            )}
                            hideFooter={true}
                        >
                            {this.state.customUtmParams.length === 0 &&
                                <div>There are no custom UTM parameters</div>
                            }
                            {this.state.customUtmParams.map(([name, config], idx) => (
                                <div key={idx} className={getClassNames('ddf-notif-editor-custom-utm')} >
                                    <Input
                                        className="custom-utm-name"
                                        size="small"
                                        type="text"
                                        placeholder="Parameter name"
                                        defaultValue={name}
                                        onKeyDown={ev => {
                                            // we should attempt to prevent spaces in the query param name
                                            if (ev.key === ' ' || ev.keyCode === 32) {
                                                ev.preventDefault();
                                                ev.stopPropagation();
                                            }
                                        }}
                                        onChange={ev => this.handleCustomUtmChange(idx, ev.target.value, config.default)}
                                    />
                                    <TemplatizedTextField
                                        className="custom-utm-value"
                                        size="small"
                                        placeholder="Parameter value"
                                        defaultValue={config.default || undefined}
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        customMacroOptions={this.state.customMacroOptions}
                                        onChange={val => this.handleCustomUtmChange(idx, name, val)}
                                    />
                                    <AntButton
                                        className="custom-utm-remove-btn"
                                        size="small"
                                        icon={<DeleteFilled />}
                                        onClick={() => this.handleCustomUtmRemove(idx)}
                                    />
                                </div>
                            ))}
                        </Well>
                    </Form>
                </Drawer>
            );
        }

        protected get domain(): DomainDto {
            return this.props.domain;
        }

        protected get appUserIsInternalDomainEditor() {
            return this.appState.abilityStore.acs
                .isInternalEditorUserScopeForDomain(this.domain.id);
        }

        protected get defaultActions(): INotificationAction[] {
            return clone(this.state.defaultActions ?? this.props.domain.defaultNotificationActions ?? []);
        }

        protected getCurrentValues(): Partial<DomainDto> {
            const values: Partial<DomainDto> = {};
            const formValues = this.props.form.getFieldsValue();

            // Landing URL Domain Whitelist
            if (!deepEqual(formValues.landingUrlDomainWhitelist ?? [], this.domain.landingUrlDomainWhitelist ?? [])) {
                values.landingUrlDomainWhitelist = formValues.landingUrlDomainWhitelist;
            }

            // Actions
            if (DEFAULT_OPEN_URL_LABEL_ENABLED) {
                if (formValues.defaultOpenUrlLabel !== this.domain.defaultNotificationActionLabels?.openUrl) {
                    const openUrl = (formValues.defaultOpenUrlLabel ?? '').toString().trim();
                    values.defaultNotificationActionLabels = !openUrl ? {} : {
                        ...(this.domain.defaultNotificationActionLabels ?? {}),
                        open_url: openUrl,
                    };
                }
            }

            const defaultActions = clone(this.state.defaultActions);
            if (!deepEqual(defaultActions, this.domain.defaultNotificationActions)) {
                const actions = defaultActions;

                // remove UI codes
                if (Array.isArray(actions)) {
                    actions.forEach((a: any) => {
                        delete a.displayMeta;

                        if (!a.usePrimaryLandingUrl) {
                            delete a.usePrimaryLandingUrl;
                        }
                    });
                }

                values.defaultNotificationActions = actions;
            }

            if (this.appUserIsInternalDomainEditor) {
                const _displayMeta: any = {
                    ...this.domain.displayMeta,
                };

                // Lifespan
                const ttlValue = formValues.ttlValue;
                const ttlMetric = formValues.ttlMetric;
                const computedTtlSeconds = calculateRawTtlSecondsFromValue(ttlValue, ttlMetric);
                if (computedTtlSeconds !== this.domain.defaultNotificationTtlSeconds) {
                    values.defaultNotificationTtlSeconds = computedTtlSeconds;
                    _displayMeta.defaultNotificationTtlMetric = ttlMetric;
                }

                // Delivery Type/Strategy
                if (!!formValues.defaultDeliveryTypeValue) {
                    _displayMeta.default_delivery_type = formValues.defaultDeliveryTypeValue;
                }
                if (!!formValues.defaultDeliveryWindowValue) {
                    _displayMeta.default_delivery_window = formValues.defaultDeliveryWindowValue;
                }

                if (!deepEqual(this.domain.displayMeta, _displayMeta)) {
                    values.displayMeta = _displayMeta;
                }

                // Media Quality
                if ((!isDef(this.domain.mediaQuality) && formValues.mediaQuality !== 40) ||
                    (isDef(this.domain.mediaQuality) && formValues.mediaQuality !== this.domain.mediaQuality)) {
                    values.mediaQuality = formValues.mediaQuality;
                }
            }

            // Media Assets
            if ((formValues.defaultIcon?.url ?? null) !== (this.domain.defaultIconUrl ?? null)) {
                values.defaultIconUrl = formValues.defaultIcon?.url === undefined ? null : formValues.defaultIcon.url;
            }
            if ((formValues.defaultBadge?.url ?? null) !== (this.domain.defaultBadgeUrl ?? null)) {
                values.defaultBadgeUrl = formValues.defaultBadge?.url === undefined ? null : formValues.defaultBadge.url;
            }

            // UTM Params
            const utmValues: any = {};
            if (formValues.utmSource) {
                utmValues.utm_source = formValues.utmSource;
            }
            if (formValues.utmContent) {
                utmValues.utm_content = formValues.utmContent;
            }
            if (formValues.utmMedium) {
                utmValues.utm_medium = formValues.utmMedium;
            }
            if (formValues.utmCampaign) {
                utmValues.utm_campaign = formValues.utmCampaign;
            }
            if (formValues.utmTerm) {
                utmValues.utm_term = formValues.utmTerm;
            }

            // handle custom utm extraction
            const addedNotificationQueryParams = {};
            if (this.state.customUtmParams.length) {
                const customUtmParams = [...this.state.customUtmParams];
                for (const [param, config] of customUtmParams) {
                    addedNotificationQueryParams[param] = config;
                }
            }

            if (!deepEqual(utmValues, this.domain.utmParams)) {
                values.utmParams = utmValues;
            }

            if (!deepEqual(addedNotificationQueryParams, this.domain.addedNotificationQueryParams)) {
                values.addedNotificationQueryParams = addedNotificationQueryParams;
            }

            return values;
        }

        protected handleFileUploadError = (res: any) => {
            const error = res?.error;

            if (error && error.error_type === 'invalid_file_type_error') {
                return simpleNotification('error', error.message);
            }
        }

        protected handleActionUpdate = async (
            action: INotificationAction,
        ) => {
            const defaultActions = this.defaultActions;

            const actionIdx = defaultActions.findIndex(a => a.ordinal === action.ordinal);
            if (actionIdx < 0) {
                defaultActions.push(action);
            } else {
                defaultActions.splice(actionIdx, 1, action);

            }
            await this.setState({ defaultActions });
        };

        protected handleActionRemove = async (
            action: INotificationAction,
        ) => {
            const defaultActions = this.defaultActions;

            const actionIdx = defaultActions.findIndex(a => a.ordinal === action.ordinal);
            if (actionIdx !== -1) {
                defaultActions.splice(actionIdx, 1);
                await this.setState({ defaultActions });
            }
        };

        protected handlePrimaryActionAdd = async (
            action: INotificationAction,
        ) => {
            action.ordinal = 0;
            this.handleActionAdd(action);
        };

        protected handleSecondaryActionAdd = async (
            action: INotificationAction,
        ) => {
            action.ordinal = 1;
            this.handleActionAdd(action);
        };

        protected async handleActionAdd(
            action: INotificationAction,
        ) {
            action.displayMeta = {
                ...action.displayMeta,
                eid: randomstring.generate(),
            }

            const defaultActions = this.defaultActions;

            const actionIdx = defaultActions.findIndex(a => a.ordinal === action.ordinal);
            if (actionIdx < 0) {
                defaultActions.push(action);
            } else {
                defaultActions.splice(actionIdx, 1, action);
            }

            await this.setState({ defaultActions });
        }

        protected handleCustomUtmAdd = () => {
            this.setState(({ customUtmParams }) => {
                const params = [...customUtmParams];
                params.push(['', {
                    default: '',
                    property: '__explicit',
                }]);

                return { customUtmParams: params };
            });
        }

        protected handleCustomUtmRemove = (
            idx: number,
        ) => {
            this.setState(({ customUtmParams }) => {
                const newValue = [...customUtmParams];
                newValue.splice(idx, 1);

                return { customUtmParams: newValue };
            });
        }

        protected handleCustomUtmChange = (
            idx: number,
            name: string,
            value: string,
        ) => {
            const customUtmParams = [...this.state.customUtmParams];

            customUtmParams[idx][0] = name.trim().replace(/\s/g, '_');
            customUtmParams[idx][1].default = value;

            this.setState({ customUtmParams });
        }

        protected handleSubmit = async () => {
            try {
                await validateFields(this.props.form);
                const mergedValues = this.getCurrentValues();

                if (mergedValues.landingUrlDomainWhitelist) {
                    mergedValues.landingUrlDomainWhitelist = prepareWhitelistDomains(
                        mergedValues.landingUrlDomainWhitelist, true);
                }

                if (this.state.customUtmParams.length) {
                    const customUtmParams = [...this.state.customUtmParams];
                    for (const [param, config] of customUtmParams) {
                        if (!param?.trim() || !config.default?.trim()) {
                            throw {
                                customUtmParams: {
                                    errors: [
                                        { message: 'Custom UTM parameter name and value are both required.' },
                                    ],
                                },
                            };
                        }
                    }

                    const uniqueParamKeys = new Set(new Map(customUtmParams).keys())
                    if (uniqueParamKeys.size < customUtmParams.length) {
                        throw {
                            customUtmParams: {
                                errors: [
                                    { message: 'Custom UTM Param names must be unique.' },
                                ],
                            },
                        };
                    }
                }

                const { ok, data } = await this.domainService.updateDomainById(
                    this.domain.id, mergedValues);

                if (ok) {
                    this.handleDrawerClose();
                    this.props.onDomainUpdated(data);
                }
            } catch (err) {
                simpleFormErrorNotification(err);
            }
        };

        protected handleDrawerClose = () => {
            this.setState({ defaultActions: undefined });
            this.props.onClose(null);
        };
    }
);

interface IDomainNotificationWell {
    loading?: boolean,
    domain: DomainDto,
    title?: string,
    onDomainUpdated: (domain: Partial<DomainDto>) => void,
}

interface IState {
    showDrawer: boolean,
    drawerKey?: string,
}

export class GeneralSettingsWell extends React.Component<IDomainNotificationWell, IState> {
    public state: IState = {
        showDrawer: false,
    };

    protected appState: AppState;

    public constructor(props: IDomainNotificationWell) {
        super(props);

        this.appState = Container.get(AppState);
    }

    public render() {
        return (
            <Well
                className="nested sw-v2-ddf-notif-actions-well"
                title="General Settings"
                actions={(
                    <Button
                        size="small"
                        onClick={this.handleDrawerOpen}
                        disabled={this.props.loading}
                    >
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                )}
                hideFooter={true}
            >
                <Skeleton
                    loading={this.props.loading}
                    active={true}
                    title={false}
                >
                    {!this.props.loading && (
                        <>
                            {this.renderTemplateWell()}
                            {this.renderActionsWell()}
                            {this.appUserIsInternalDomainEditor && this.renderTtlWell()}
                            {this.renderMediaWell()}
                            {this.renderUtmWell()}
                            {this.renderCustomUtmParamsWell()}

                            <DomainNotificationEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        );
    }

    protected get appUserIsInternalDomainEditor() {
        return this.appState.abilityStore.acs
            .isInternalEditorUserScopeForDomain(this.domain.id);
    }

    protected renderTemplateWell() {
        const whitelist = this.domain.landingUrlDomainWhitelist ?? [];

        return (
            <Well
                title="Landing URLs"
                mode="ghost"
                hideFooter={true}
            >
                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        Domain Whitelist
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        {whitelist.join(', ') || '* (Any domain)'}
                    </div>
                    <div className={getClassNames('form-layout-row-sub')}>
                        <WarningOutlined />
                        <span>
                            Whitelisting domains allows control over which domains
                            can be used when creating landing URLs.
                        </span>
                    </div>
                </div>
            </Well>
        );
    }

    protected renderActionsWell() {
        const domainDefaultLabels = getDomainDefaultActionLabels(this.domain);
        const primaryAction: any = parseDefaultNotificationAction(this.domain, 0);
        const secondaryAction: any = parseDefaultNotificationAction(this.domain, 1);

        return (
            <Well
                title="Button Defaults"
                mode="ghost"
                hideFooter={true}
            >
                {DEFAULT_OPEN_URL_LABEL_ENABLED && (
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>
                            Open URL Label
                        </div>
                        <div className={getClassNames('form-layout-row-value')}>
                            {domainDefaultLabels.openUrl}
                        </div>
                    </div>
                )}

                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        Buttons
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        <div className={getClassNames('notif-action-builder-collection')}>
                            <NotificationActionBuilder
                                mode="static"
                                enabled={true}
                                title="Button 1"
                                config={primaryAction}
                            />
                            <NotificationActionBuilder
                                mode="static"
                                enabled={!!secondaryAction}
                                title="Button 2"
                                noActionText="No Default Button 2"
                                config={secondaryAction}
                            />
                        </div>
                    </div>
                </div>
            </Well>
        );
    }

    protected renderTtlWell() {
        return (
            <Well
                title="Delivery Defaults"
                mode="ghost"
                hideFooter={true}
            >
                <TtlDisplayElement label="Lifespan" entity={this.domain} />
                <DomainDeliveryDisplay
                    defaults={{
                        type: this.domain.displayMeta?.default_delivery_type,
                        window: this.domain.displayMeta?.default_delivery_window
                    }}
                    entity={this.domain}
                />
            </Well>
        );
    }

    protected renderMediaWell() {
        return (
            <Well
                title="Media Defaults"
                mode="ghost"
                hideFooter={true}
            >
                {this.appUserIsInternalDomainEditor && (
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>
                            Image Upload Quality
                        </div>
                        <div className={getClassNames('form-layout-row-value')}>
                            {this.domain.mediaQuality || 40}%
                        </div>
                        <div className={getClassNames('form-layout-row-sub')}>
                            <InfoCircleOutlined />
                            <span>
                                Quality can be set anywhere between 30 & 100.
                                Values lower than 40 may significantly impact images.
                            </span>
                        </div>
                    </div>
                )}

                <div className="media-row">
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>
                            Notification Icon
                        </div>
                        <div className={getClassNames('form-layout-row-value')}>
                            {!this.domain.defaultIconUrl ? 'No icon' : (
                                <div className={getClassNames('media-display')}>
                                    <div className={getClassNames('media-display-image')} style={{
                                        backgroundImage: `url("${this.domain.defaultIconUrl}")`
                                    }} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>
                            Notification Badge
                        </div>
                        <div className={getClassNames('form-layout-row-value')}>
                            {!this.domain.defaultBadgeUrl ? 'No badge' : (
                                <div className={getClassNames('media-display')}>
                                    <div className={getClassNames('media-display-image')} style={{
                                        backgroundImage: `url("${this.domain.defaultBadgeUrl}")`
                                    }} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Well>
        );
    }

    protected renderUtmWell() {
        const utmParams: any = this.domain.utmParams || {};

        return (
            <Well
                title="UTM Parameter Defaults"
                mode="ghost"
                hideFooter={true}
            >
                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        <NoTranslate>utm_source</NoTranslate>
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        <NoTranslate>{utmParams.utm_source && utmParams.utm_source}</NoTranslate>
                    </div>
                </div>

                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        <NoTranslate>utm_medium</NoTranslate>
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        <NoTranslate>{utmParams.utm_medium && utmParams.utm_medium}</NoTranslate>
                    </div>
                </div>

                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        <NoTranslate>utm_campaign</NoTranslate>
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        <NoTranslate>{utmParams.utm_campaign && utmParams.utm_campaign}</NoTranslate>
                    </div>
                </div>

                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        <NoTranslate>utm_term</NoTranslate>
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        <NoTranslate>{utmParams.utm_term && utmParams.utm_term}</NoTranslate>
                    </div>
                </div>

                <div className={getClassNames('form-layout-row')}>
                    <div className={getClassNames('form-layout-row-label')}>
                        <NoTranslate>utm_content</NoTranslate>
                    </div>
                    <div className={getClassNames('form-layout-row-value')}>
                        <NoTranslate>{utmParams.utm_content && utmParams.utm_content}</NoTranslate>
                    </div>
                </div>
            </Well>
        );
    }

    protected renderCustomUtmParamsWell() {
        const addedNotificationQueryParams = this.domain.addedNotificationQueryParams ?? {};
        return (
            <Well
                title="UTM Custom Parameters"
                className="custom-utm-parameter-well"
                mode="ghost"
                hideFooter={true}
            >
                {Object.keys(addedNotificationQueryParams).length === 0 &&
                    <div>There are no custom UTM parameters</div>
                }
                {Object.entries(addedNotificationQueryParams).map(([name, config], idx) => (
                    <div key={idx} className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>
                            <NoTranslate>{name}</NoTranslate>
                        </div>
                        <div className={getClassNames('form-layout-row-value')}>
                            <NoTranslate>{config.default}</NoTranslate>
                        </div>
                    </div>
                ))}
            </Well>
        );
    }

    protected get domain(): DomainDto {
        return this.props.domain;
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() });
    };

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false });
    };
}
